<script setup>
/* eslint-disable */
import { ref } from 'vue';
import { useUnreadBadgeSettings } from 'dashboard/composables/useUnreadBadgeSettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SmartUnreadBadge from 'dashboard/components-next/Conversation/SmartUnreadBadge.vue';

const { t } = useI18n();

const { badgeColor, updateBadgeColor, resetToDefaults, availableColors } =
  useUnreadBadgeSettings();

const previewCount = ref(5);

const handleColorChange = color => {
  updateBadgeColor(color);
  useAlert(t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.COLOR_UPDATED'), {
    type: 'info',
  });
};

const handleReset = () => {
  resetToDefaults();
  useAlert(t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.RESET_SUCCESS'));
};

const setPreviewCount = count => {
  previewCount.value = count;
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Color Selection -->
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.COLOR_LABEL') }}
        </label>
        <p class="text-sm text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.COLOR_DESCRIPTION') }}
        </p>
      </div>
      <div class="grid grid-cols-4 gap-3 sm:grid-cols-8">
        <button
          v-for="color in availableColors"
          :key="color.value"
          class="flex flex-col items-center gap-2 p-3 transition-all duration-200 rounded-lg hover:bg-n-alpha-2"
          :class="
            badgeColor === color.value
              ? 'bg-n-alpha-2 ring-2 ring-blue-500/60'
              : ''
          "
          @click="handleColorChange(color.value)"
        >
          <div
            class="w-8 h-8 rounded-full shadow-sm"
            :class="color.value"
            :style="{ backgroundColor: color.hex }"
          />
          <span
            v-if="badgeColor === color.value"
            class="text-xs font-medium text-blue-500"
          >
            <Icon icon="check" class="size-4" />
          </span>
        </button>
      </div>
    </div>

    <!-- Animation Toggle -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.ANIMATION_LABEL') }}
      </label>
      <p class="text-sm text-n-slate-11">
        {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.ANIMATION_DISABLED_INFO') }}
      </p>
    </div>

    <!-- Preview -->
    <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.PREVIEW_LABEL') }}
        </label>
        <p class="text-sm text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.PREVIEW_DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-4">
        <SmartUnreadBadge :count="previewCount" />
        <div class="flex gap-2">
          <button
            v-for="count in [1, 5, 10, 50]"
            :key="count"
            class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            :class="
              previewCount === count
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
            "
            @click="setPreviewCount(count)"
          >
            {{ count }}
          </button>
        </div>
      </div>
      <div class="flex gap-2 text-xs text-n-slate-10">
        <Icon icon="info" class="flex-shrink-0 mt-0.5 size-4" />
        <span>
          {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.PREVIEW_INFO') }}
        </span>
      </div>
    </div>

    <!-- Reset Button -->
    <div class="flex justify-start">
      <button
        class="px-4 py-2 text-sm font-medium transition-colors rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
        @click="handleReset"
      >
        {{ $t('PROFILE_SETTINGS.FORM.UNREAD_BADGE.RESET_BUTTON') }}
      </button>
    </div>
  </div>
</template>
